/**
 * The conversation list on the left of the AI Studio, as in ChatGPT (Robert, 18.10.2026): "New conversation" (always
 * there; in an empty new conversation it does nothing), the conversations grouped by day and a "…" menu per
 * conversation (rename in a dialog, delete). The search icon in the head opens a search dialog: recent conversations,
 * then the matches in names and messages (through the backend). Build lists the conversations with Mo, Test the test
 * chats. Collapsible to a slim rail; on narrow screens the chat head's picker stays instead (CSS).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, InputBase, Modal, TextField, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft, faAnglesRight, faEllipsis, faMagnifyingGlass, faPen, faPenToSquare, faSpinner, faTrash, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faMessage } from '@fortawesome/free-regular-svg-icons'; // the outlined bubble, as in ChatGPT's search
import ConfirmationModal from '../confirmationModal';
import '../confirmationModal.css'; // .modal-title, as in the platform's modals
import { guardWrite } from '../../utils/permissions';
import { useBoxSx } from './PackageLibrary';
import { Spinner } from './ui';

const SEARCH_DELAY_MS = 250;
const RECENT = 12; // the search dialog's list before anything is typed
const DAY_MS = 24 * 3600 * 1000;
const OPEN_KEY = 'agentStudio.convSide';

/** open or collapsed, one choice for Build and Test; open unless the owner closed it (as in ChatGPT) */
export function useConversationSide() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(OPEN_KEY) !== '0'; } catch (_) { return true; }
  });
  const toggle = (next) => {
    setOpen(next);
    try { localStorage.setItem(OPEN_KEY, next ? '1' : '0'); } catch (_) { /* storage unavailable */ }
  };
  return [open, toggle];
}

/** seconds (Mo's list) or an ISO date (test chats) -> milliseconds */
export function toMs(v) {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
  const ms = Date.parse(v);
  return Number.isNaN(ms) ? 0 : ms;
}

/** today, yesterday, the previous 7 and 30 days, older – by the conversation's last change */
export function groupByDay(conversations, now = Date.now()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const today = start.getTime();
  const order = ['today', 'yesterday', 'week', 'month', 'older'];
  const groups = Object.fromEntries(order.map((k) => [k, []]));
  for (const c of conversations) {
    const at = toMs(c.updated_at);
    const key = at >= today ? 'today' : at >= today - DAY_MS ? 'yesterday' : at >= today - 7 * DAY_MS ? 'week'
      : at >= today - 30 * DAY_MS ? 'month' : 'older';
    groups[key].push(c);
  }
  return order.filter((k) => groups[k].length).map((k) => ({ key: k, items: groups[k] }));
}

function Highlight({ text, q }) {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

/** Search like ChatGPT's: the field on top, the recent conversations, then the matches (names and messages). */
export function SearchDialog({ conversations, onSearch, onOpen, onClose }) {
  const { t } = useTranslation();
  const sx = useBoxSx(640);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const search = useRef(onSearch);
  search.current = onSearch;
  const name = (c) => c?.title || t('agent_studio_conversation_untitled');

  useEffect(() => {
    const text = q.trim();
    setActive(0);
    if (!text) { setResults(null); setSearching(false); return undefined; }
    let alive = true;
    setSearching(true);
    const id = setTimeout(() => {
      Promise.resolve(search.current(text))
        .then((r) => alive && setResults((r || []).map((x) => ({ ...x, id: x.id ?? x.session_id }))))
        .catch(() => alive && setResults([])).finally(() => alive && setSearching(false));
    }, SEARCH_DELAY_MS);
    return () => { alive = false; clearTimeout(id); };
  }, [q]);

  const list = results ?? conversations.slice(0, RECENT);
  const open = (c) => { onOpen(c.id); onClose(); };
  const keys = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, list.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && list[active]) { e.preventDefault(); open(list[active]); }
  };

  return (
    <Modal open onClose={onClose}>
      <Box sx={{ ...sx, p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} className="as-modal"
        role="dialog" aria-label={t('as_side_search')}>
        <div className="as-search-head">
          <FontAwesomeIcon icon={searching ? faSpinner : faMagnifyingGlass} spin={searching} className="as-search-ic" />
          <InputBase autoFocus fullWidth value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={keys}
            placeholder={t('as_side_search_placeholder')} className="as-search-input"
            inputProps={{ 'aria-label': t('as_side_search'), maxLength: 100 }} />
          <button type="button" className="as-search-close" onClick={onClose} aria-label={t('close')} title={t('close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="as-search-body">
          {results === null && <div className="as-search-section">{t('as_side_recent')}</div>}
          {list.length === 0 ? (
            !searching && <p className="as-side-empty">{results === null ? t('agent_studio_test_no_conversations') : t('as_side_no_results')}</p>
          ) : (
            <ul className="as-search-list" role="listbox" aria-label={results === null ? t('as_side_recent') : t('as_side_search')}>
              {list.map((c, i) => (
                <li key={c.id} role="option" aria-selected={i === active}>
                  <button type="button" className={`as-search-item ${i === active ? 'is-active' : ''}`}
                    onMouseEnter={() => setActive(i)} onClick={() => open(c)}>
                    <FontAwesomeIcon icon={faMessage} className="as-search-item-ic" />
                    <span className="as-search-item-text">
                      <span className="as-search-item-title"><Highlight text={name(c)} q={q.trim()} /></span>
                      {c.snippet && <span className="as-search-item-snippet"><Highlight text={c.snippet} q={q.trim()} /></span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Box>
    </Modal>
  );
}

// the platform's text field in modals (user management, Team.js: inputSx), so it looks the same everywhere
const INPUT_SX = {
  "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { border: "1px solid #515151" } },
  "& .MuiInputLabel-root": { color: "gray", "&.Mui-focused": { color: "#5616ea" } },
};

/** "Rename conversation", built exactly like the platform's modals (Team.js: create / edit workspace):
 *  title, a line of text, the labelled text field, Cancel and Save. Enter saves. */
export function RenameDialog({ conversation, onSave, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [draft, setDraft] = useState(conversation.title || '');
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e?.preventDefault();
    if (saving) return;
    if (draft.trim() === (conversation.title || '').trim()) { onClose(); return; }
    setSaving(true);
    try { await onSave(conversation.id, draft.trim()); } finally { setSaving(false); onClose(); }
  };
  return (
    <Modal open onClose={() => !saving && onClose()}>
      <Box
        component="form"
        onSubmit={save}
        role="dialog"
        aria-labelledby="as-rename-title"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          borderRadius: "10px",
          boxShadow: 24,
          p: 4,
          padding: isMobile ? "20px" : "30px",
          width: isMobile ? "80%" : "90%",
          maxWidth: 400,
        }}
      >
        <div className="modal-title">
          <h2 id="as-rename-title">{t('as_side_rename_title')}</h2>
        </div>
        <p>{t('as_side_rename_hint')}</p>
        <TextField
          autoFocus
          fullWidth
          label={t('as_side_rename_label')}
          placeholder={t('agent_studio_conversation_untitled')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => e.target.select()}
          inputProps={{ maxLength: 80 }}
          sx={INPUT_SX}
        />
        {/* the buttons as in "Add Behaviors" (addBehaviorModal.js) */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button
            type="button"
            onClick={onClose}
            disabled={saving}
            variant="outlined"
            sx={{
              color: '#5616ea',
              borderColor: '#5616ea',
              '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: '#5616ea',
              '&:hover': { backgroundColor: '#4a14d6' },
            }}
          >
            {saving ? <Spinner size={14} /> : t('save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default function ConversationSidebar({
  title, conversations, currentId, runningId, onOpen, onNew, onDelete, onRename, onSearch, open, onToggle,
}) {
  const { t } = useTranslation();
  const [menuFor, setMenuFor] = useState(null); // the conversation whose "…" menu is open
  const [renaming, setRenaming] = useState(null); // the conversation in the rename dialog
  const [searchOpen, setSearchOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const name = (c) => c?.title || t('agent_studio_conversation_untitled');
  const locked = (c) => !!runningId && c.id === runningId; // Mo is working in it
  const groups = useMemo(() => groupByDay(conversations), [conversations]);

  const startRename = (c) => {
    setMenuFor(null);
    if (guardWrite()) setRenaming(c);
  };
  const askDelete = (c) => {
    setMenuFor(null);
    if (guardWrite()) setToDelete(c);
  };

  const dialogs = (
    <>
      {searchOpen && (
        <SearchDialog conversations={conversations} onSearch={onSearch} onOpen={onOpen} onClose={() => setSearchOpen(false)} />
      )}
      {renaming && <RenameDialog conversation={renaming} onSave={onRename} onClose={() => setRenaming(null)} />}
      <ConfirmationModal
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => onDelete(toDelete.id)}
        itemName={toDelete ? t('agent_studio_conversation_item', { title: name(toDelete) }) : ''}
      />
    </>
  );

  if (!open) {
    return (
      <nav className="as-convo-side is-collapsed" aria-label={title}>
        <button type="button" className="as-side-icon" onClick={() => onToggle(true)} title={t('as_side_show')}
          aria-label={t('as_side_show')} data-pill-anchor>
          <FontAwesomeIcon icon={faAnglesRight} />
        </button>
        <button type="button" className="as-side-icon" onClick={onNew}
          title={t('agent_studio_test_new_conversation')} aria-label={t('agent_studio_test_new_conversation')}>
          <FontAwesomeIcon icon={faPenToSquare} />
        </button>
        <button type="button" className="as-side-icon" title={t('as_side_search')} aria-label={t('as_side_search')}
          onClick={() => setSearchOpen(true)}>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </button>
        {dialogs}
      </nav>
    );
  }

  return (
    <nav className="as-convo-side" aria-label={title}>
      <div className="as-side-head">
        <span className="as-side-heading">{title}</span>
        <button type="button" className="as-side-icon is-small" onClick={() => setSearchOpen(true)}
          title={t('as_side_search')} aria-label={t('as_side_search')}>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </button>
        <button type="button" className="as-side-icon is-small" onClick={() => onToggle(false)} title={t('as_side_hide')}
          aria-label={t('as_side_hide')}>
          <FontAwesomeIcon icon={faAnglesLeft} />
        </button>
      </div>
      <button type="button" className="as-side-new" onClick={onNew} data-pill-anchor>
        <FontAwesomeIcon icon={faPenToSquare} /> {t('agent_studio_test_new_conversation')}
      </button>

      <div className="as-side-list">
        {conversations.length === 0 ? (
          <p className="as-side-empty">{t('agent_studio_test_no_conversations')}</p>
        ) : groups.map((g) => (
          <section key={g.key} className="as-side-group" aria-label={t(`as_side_${g.key}`)}>
            <h4 className="as-side-group-title">{t(`as_side_${g.key}`)}</h4>
            {g.items.map((c) => {
              const current = c.id === currentId;
              return (
                <div key={c.id} className={`as-side-row ${current ? 'is-current' : ''} ${menuFor === c.id ? 'is-menu' : ''}`}>
                  <button type="button" className="as-side-item" aria-current={current ? 'page' : undefined}
                    onClick={() => onOpen(c.id)}>
                    <span className="as-side-title">{name(c)}</span>
                    {locked(c) && <span className="as-side-running" title={t('agent_studio_running_badge')} />}
                  </button>
                  <span className="as-side-more-wrap">
                    <button type="button" className="as-side-more" aria-label={t('as_side_more')} title={t('as_side_more')}
                      aria-expanded={menuFor === c.id} onClick={() => setMenuFor(menuFor === c.id ? null : c.id)}>
                      <FontAwesomeIcon icon={faEllipsis} />
                    </button>
                    {menuFor === c.id && (
                      <>
                        <div className="as-menu-backdrop" onClick={() => setMenuFor(null)} role="presentation" />
                        <div className="as-side-menu" role="menu">
                          <button type="button" role="menuitem" onClick={() => startRename(c)}>
                            <FontAwesomeIcon icon={faPen} /> {t('as_side_rename')}
                          </button>
                          {!locked(c) && (
                            <button type="button" role="menuitem" className="is-danger" onClick={() => askDelete(c)}>
                              <FontAwesomeIcon icon={faTrash} /> {t('agent_studio_delete')}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </section>
        ))}
      </div>
      {dialogs}
    </nav>
  );
}
